import {Copy, Mail} from 'lucide-react';
import {useLayoutEffect, useRef, useState} from 'react';
import {useToast} from '@/matic/components/ui/toast';

type RelayForwardingAddressCardProps = {
  address: string;
};

const BASE_FONT_SIZE_PX = 10;
const MIN_FONT_SIZE_PX = 10;
const MAX_FONT_SIZE_PX = 15;
const HORIZONTAL_PADDING_PX = 8; // Px-1 (0.25rem) on each side

/** Grows the address font size to fill the available width on one line, without overflowing. */
function useFitTextFontSize(
  text: string,
  elementRef: React.RefObject<HTMLElement | null>
) {
  const [fontSize, setFontSize] = useState(BASE_FONT_SIZE_PX);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const measure = () => {
      const availableWidth = element.clientWidth - HORIZONTAL_PADDING_PX;
      if (availableWidth <= 0) return;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const style = globalThis.getComputedStyle(element);
      ctx.font = `${style.fontWeight} ${BASE_FONT_SIZE_PX}px ${style.fontFamily}`;
      const textWidth = ctx.measureText(text).width;
      if (textWidth <= 0) return;

      const scale = availableWidth / textWidth;
      const next = Math.min(
        MAX_FONT_SIZE_PX,
        Math.max(MIN_FONT_SIZE_PX, BASE_FONT_SIZE_PX * scale)
      );
      setFontSize(next);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [text, elementRef]);

  return fontSize;
}

export function RelayForwardingAddressCard({
  address,
}: RelayForwardingAddressCardProps) {
  const {toast} = useToast();
  const addressRef = useRef<HTMLElement>(null);
  const fontSize = useFitTextFontSize(address, addressRef);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast({title: 'Forwarding address copied', variant: 'success'});
    } catch {
      toast({title: "Couldn't copy address", variant: 'error'});
    }
  };

  return (
    <div className="mx-2 mb-2 shrink-0 rounded-md border border-primary-500/25 bg-sidebar-active/45 p-2">
      <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase text-sidebar-fg/60">
        <Mail className="h-3 w-3" />
        Forward emails to Relay
      </div>
      <div className="flex items-center gap-0.5">
        <code
          ref={addressRef}
          style={{fontSize}}
          className="min-w-0 flex-1 truncate whitespace-nowrap rounded bg-black/10 px-1 py-0.5 font-medium tracking-tight text-white"
        >
          {address}
        </code>
        <button
          type="button"
          title="Copy forwarding address"
          aria-label="Copy forwarding address"
          className="shrink-0 rounded p-0.5 text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          onClick={() => {
            void handleCopy();
          }}
        >
          <Copy className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
